import { addDays, addWeeks, addMonths } from "date-fns";
import { BarChart } from "lucide-react";
import { useMemo } from "react";
import { DateRange } from "react-day-picker";
import { Area, AreaChart, CartesianGrid, XAxis, YAxis } from "recharts";
import {
    getDateKeyForGrouping,
    getWeekStartKey,
    getMonthKeyForGrouping,
    getGroupingGranularity,
    formatDateForDisplay,
    formatWeekRange,
} from "./utils";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    ChartContainer,
    ChartTooltip,
    ChartTooltipContent,
    ChartLegend,
    ChartLegendContent,
} from "@/components/ui/chart";
import { useAppSelector } from "@/hooks/types";
import { CreditTransactionAnalytics } from "@/pages/Admin/Stats/StatsPage";
import { getIs1TemplateTenant } from "@/store/tenant/selectors";
import { ICreditTransaction } from "@/types/creditTransaction";

interface UsageAnalyticsProps {
    data: {
        analytics: CreditTransactionAnalytics;
        transactions: ICreditTransaction[];
    };
    dateRange: DateRange;
}

const chartConfig = {
    SPEND: {
        label: "Spend",
        theme: {
            light: "hsl(var(--admin-chart-1))",
            dark: "hsl(var(--admin-chart-1))",
        },
    },
    EARN: {
        label: "Earn",
        theme: {
            light: "hsl(var(--admin-chart-2))",
            dark: "hsl(var(--admin-chart-2))",
        },
    },
    REFUND: {
        label: "Refund",
        theme: {
            light: "hsl(var(--admin-chart-3))",
            dark: "hsl(var(--admin-chart-3))",
        },
    },
    PURCHASE: {
        label: "Purchase",
        theme: {
            light: "hsl(var(--admin-chart-4))",
            dark: "hsl(var(--admin-chart-4))",
        },
    },
    ADMIN: {
        label: "Admin",
        theme: {
            light: "hsl(var(--admin-chart-5))",
            dark: "hsl(var(--admin-chart-5))",
        },
    },
    SIGNUP: {
        label: "Signup",
        theme: {
            light: "hsl(var(--admin-chart-6))",
            dark: "hsl(var(--admin-chart-6))",
        },
    },
};

const tenantTransactionTypes = ["SPEND", "EARN", "ADMIN", "SIGNUP"];

const TemplateOnlyTypes = ["PURCHASE", "REFUND"];

export const UsageAnalytics = ({ data, dateRange }: UsageAnalyticsProps) => {
    // Process transaction data by type over time
    const from = useMemo(
        () => dateRange?.from ?? new Date(),
        [dateRange?.from],
    );
    const to = useMemo(() => dateRange?.to ?? new Date(), [dateRange?.to]);
    const granularity = getGroupingGranularity(from, to);
    const is1TemplateTenant = useAppSelector(getIs1TemplateTenant);
    const transactionTypes = useMemo(() => {
        return is1TemplateTenant
            ? [...tenantTransactionTypes, ...TemplateOnlyTypes]
            : tenantTransactionTypes;
    }, [is1TemplateTenant]);

    const transactionTrendsData = useMemo(() => {
        if (!data?.transactions) return [];

        // Group transactions by the chosen granularity
        const groupKeyFn =
            granularity === "month"
                ? getMonthKeyForGrouping
                : granularity === "week"
                  ? getWeekStartKey
                  : getDateKeyForGrouping;

        const groupedTypeCounts = data.transactions.reduce(
            (acc, transaction) => {
                const key = groupKeyFn(transaction.createdAt);
                const type = transaction.type;
                if (!acc[key]) {
                    acc[key] = {};
                }
                acc[key][type] = (acc[key][type] || 0) + 1;
                return acc;
            },
            {} as Record<string, Record<string, number>>,
        );

        // Generate complete date range
        const generateDateRange = () => {
            const dates: string[] = [];
            let currentDate = new Date(from);
            const endDate = new Date(to);

            while (currentDate <= endDate) {
                let key: string;

                if (granularity === "month") {
                    key = getMonthKeyForGrouping(currentDate);
                    // Move to next month
                    currentDate = addMonths(currentDate, 1);
                } else if (granularity === "week") {
                    key = getWeekStartKey(currentDate);
                    // Move to next week
                    currentDate = addWeeks(currentDate, 1);
                } else {
                    key = getDateKeyForGrouping(currentDate);
                    // Move to next day
                    currentDate = addDays(currentDate, 1);
                }

                if (!dates.includes(key)) {
                    dates.push(key);
                }
            }

            return dates;
        };

        const completeDateRange = generateDateRange();

        return completeDateRange
            .map(key => {
                const dataPoint: Record<string, number | string> = {
                    key: String(key),
                };
                transactionTypes.forEach(type => {
                    dataPoint[type] = groupedTypeCounts[key]?.[type] || 0;
                });
                return dataPoint;
            })
            .sort((a, b) => String(a.key).localeCompare(String(b.key)));
    }, [data.transactions, granularity, from, to, transactionTypes]);

    const totalTransactions = transactionTrendsData.reduce(
        (sum, item) =>
            sum +
            Object.values(item)
                .filter(val => typeof val === "number")
                .reduce((a, b) => a + b, 0),
        0,
    );

    return (
        <Card className="h-full">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <BarChart className="h-5 w-5" />
                    Usage Analytics
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="rounded-lg bg-admin-muted p-4">
                    <div className="mb-2 flex items-center justify-between">
                        <div className="flex flex-col">
                            <h4 className="text-sm font-semibold">
                                Action Trends Over Time
                            </h4>
                            <p className="text-sm text-admin-muted-foreground">
                                Daily actions by type
                            </p>
                        </div>
                        <div className="mt-4 flex items-center gap-4 pr-4 text-xs text-admin-muted-foreground">
                            <span className="font-semibold">
                                Total Transactions:
                            </span>
                            {totalTransactions}
                        </div>
                    </div>

                    {transactionTrendsData.length > 0 ? (
                        <ChartContainer
                            config={chartConfig}
                            className="h-64 w-full"
                        >
                            <AreaChart
                                data={transactionTrendsData}
                                {...{ overflow: "visible" }}
                            >
                                <defs>
                                    <linearGradient
                                        id="fillSPEND"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="5%"
                                            stopColor="hsl(var(--admin-chart-1))"
                                            stopOpacity={0.8}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor="hsl(var(--admin-chart-1))"
                                            stopOpacity={0.1}
                                        />
                                    </linearGradient>
                                    <linearGradient
                                        id="fillEARN"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="5%"
                                            stopColor="hsl(var(--admin-chart-2))"
                                            stopOpacity={0.8}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor="hsl(var(--admin-chart-2))"
                                            stopOpacity={0.1}
                                        />
                                    </linearGradient>
                                    <linearGradient
                                        id="fillREFUND"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="5%"
                                            stopColor="hsl(var(--admin-chart-3))"
                                            stopOpacity={0.8}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor="hsl(var(--admin-chart-3))"
                                            stopOpacity={0.1}
                                        />
                                    </linearGradient>
                                    <linearGradient
                                        id="fillPURCHASE"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="5%"
                                            stopColor="hsl(var(--admin-chart-4))"
                                            stopOpacity={0.8}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor="hsl(var(--admin-chart-4))"
                                            stopOpacity={0.1}
                                        />
                                    </linearGradient>
                                    <linearGradient
                                        id="fillADMIN"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="5%"
                                            stopColor="hsl(var(--admin-chart-5))"
                                            stopOpacity={0.8}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor="hsl(var(--admin-chart-5))"
                                            stopOpacity={0.1}
                                        />
                                    </linearGradient>
                                    <linearGradient
                                        id="fillSIGNUP"
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="5%"
                                            stopColor="hsl(var(--admin-chart-1))"
                                            stopOpacity={0.8}
                                        />
                                        <stop
                                            offset="95%"
                                            stopColor="hsl(var(--admin-chart-1))"
                                            stopOpacity={0.1}
                                        />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid vertical={false} />
                                <XAxis
                                    dataKey="key"
                                    tickLine={false}
                                    axisLine={false}
                                    tick={{ fontSize: 10 }}
                                    tickMargin={8}
                                    minTickGap={32}
                                    tickFormatter={value => {
                                        if (granularity === "month") {
                                            // value: yyyy-MM
                                            const [year, month] =
                                                String(value).split("-");
                                            return `${new Date(Number(year), Number(month) - 1).toLocaleString("en-US", { month: "short", year: "2-digit" })}`;
                                        } else if (granularity === "week") {
                                            // value: yyyy-MM-dd (start of week)
                                            return formatWeekRange(
                                                String(value),
                                            );
                                        } else {
                                            // day granularity
                                            return formatDateForDisplay(
                                                value,
                                                "MMM d",
                                            );
                                        }
                                    }}
                                />
                                <YAxis tick={{ fontSize: 12 }} />
                                <ChartTooltip
                                    cursor={false}
                                    content={
                                        <ChartTooltipContent
                                            labelFormatter={value => {
                                                return new Date(
                                                    value,
                                                ).toLocaleDateString("en-US", {
                                                    month: "short",
                                                    day: "numeric",
                                                });
                                            }}
                                            indicator="dot"
                                        />
                                    }
                                />
                                <Area
                                    dataKey="SIGNUP"
                                    type="natural"
                                    fill="url(#fillSIGNUP)"
                                    stroke="hsl(var(--admin-chart-1))"
                                    stackId="a"
                                />
                                <Area
                                    dataKey="ADMIN"
                                    type="natural"
                                    fill="url(#fillADMIN)"
                                    stroke="hsl(var(--admin-chart-5))"
                                    stackId="a"
                                />
                                {is1TemplateTenant && (
                                    <Area
                                        dataKey="PURCHASE"
                                        type="natural"
                                        fill="url(#fillPURCHASE)"
                                        stroke="hsl(var(--admin-chart-4))"
                                        stackId="a"
                                    />
                                )}
                                {is1TemplateTenant && (
                                    <Area
                                        dataKey="REFUND"
                                        type="natural"
                                        fill="url(#fillREFUND)"
                                        stroke="hsl(var(--admin-chart-3))"
                                        stackId="a"
                                    />
                                )}
                                <Area
                                    dataKey="EARN"
                                    type="natural"
                                    fill="url(#fillEARN)"
                                    stroke="hsl(var(--admin-chart-2))"
                                    stackId="a"
                                />
                                <Area
                                    dataKey="SPEND"
                                    type="natural"
                                    fill="url(#fillSPEND)"
                                    stroke="hsl(var(--admin-chart-1))"
                                    stackId="a"
                                />
                                <ChartLegend content={<ChartLegendContent />} />
                            </AreaChart>
                        </ChartContainer>
                    ) : (
                        <div className="flex h-64 items-center justify-center text-admin-muted-foreground">
                            No transaction data available for the selected
                            period
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    );
};
